import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

import type {
  TextQualityReport,
  TextQualityScan,
  TextQualitySave,
} from "@lingoshade/domain";

interface QualityState {
  includeUserTexts: boolean;
  loadRequestId: string | null;
  items: TextQualityReport[];
  selected: TextQualityReport | null;
  scanned: number;
  running: boolean;
  loading: boolean;
  saving: boolean;
  completed: boolean;
  started: boolean;
  error: string | null;
  saved: boolean;
}
const initialState: QualityState = {
  includeUserTexts: false,
  loadRequestId: null,
  items: [],
  selected: null,
  scanned: 0,
  running: false,
  loading: false,
  saving: false,
  completed: false,
  started: false,
  error: null,
  saved: false,
};

export const scanQuality = createAsyncThunk<
  void,
  boolean,
  { rejectValue: string }
>(
  "textQuality/scan",
  async (
    includeUserTexts,
    { dispatch, signal, rejectWithValue },
  ): Promise<void | ReturnType<typeof rejectWithValue>> => {
    try {
      let cursor: string | undefined;
      do {
        const response = await adminApiClient.post<TextQualityScan>(
          "/admin/text-quality/scan",
          { includeUserTexts, cursor },
          { signal, timeout: 30000 },
        );
        dispatch(qualityBatch(response.data));
        cursor = response.data.nextCursor ?? undefined;
      } while (cursor && !signal.aborted);
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
export const loadQualityText = createAsyncThunk<
  TextQualityReport,
  string,
  { rejectValue: string }
>(
  "textQuality/load",
  async (
    id,
    { rejectWithValue },
  ): Promise<TextQualityReport | ReturnType<typeof rejectWithValue>> => {
    try {
      return (
        await adminApiClient.get<TextQualityReport>(
          `/admin/text-quality/${encodeURIComponent(id)}`,
        )
      ).data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
export const saveQualityText = createAsyncThunk<
  TextQualityReport,
  { id: string; data: TextQualitySave },
  { rejectValue: string }
>(
  "textQuality/save",
  async (
    { id, data },
    { rejectWithValue },
  ): Promise<TextQualityReport | ReturnType<typeof rejectWithValue>> => {
    try {
      return (
        await adminApiClient.put<TextQualityReport>(
          `/admin/text-quality/${encodeURIComponent(id)}`,
          data,
          { timeout: 150000 },
        )
      ).data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);
const slice = createSlice({
  name: "textQuality",
  initialState,
  reducers: {
    qualityBatch: (state, action: { payload: TextQualityScan }): void => {
      state.items.push(...action.payload.items);
      state.scanned += action.payload.scanned;
    },
  },
  extraReducers: (builder): void => {
    builder
      .addCase(scanQuality.pending, (state, action): void => {
        state.includeUserTexts = action.meta.arg;
        state.items = [];
        state.scanned = 0;
        state.running = true;
        state.completed = false;
        state.started = true;
        state.error = null;
      })
      .addCase(scanQuality.fulfilled, (state): void => {
        state.running = false;
        state.completed = true;
      })
      .addCase(scanQuality.rejected, (state, action): void => {
        state.running = false;
        state.error = action.meta.aborted ? null : (action.payload ?? null);
      })
      .addCase(loadQualityText.pending, (state, action): void => {
        state.loadRequestId = action.meta.requestId;
        state.loading = true;
        state.selected = null;
        state.error = null;
        state.saved = false;
      })
      .addCase(loadQualityText.fulfilled, (state, action): void => {
        if (state.loadRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.selected = action.payload;
      })
      .addCase(loadQualityText.rejected, (state, action): void => {
        if (state.loadRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(saveQualityText.pending, (state): void => {
        state.saving = true;
        state.error = null;
        state.saved = false;
      })
      .addCase(saveQualityText.fulfilled, (state, action): void => {
        state.saving = false;
        state.saved = true;
        state.selected = action.payload;
        state.items = state.items
          .map(
            (item): TextQualityReport =>
              item.id === action.payload.id ? action.payload : item,
          )
          .filter((item): boolean => item.issues.length > 0);
      })
      .addCase(saveQualityText.rejected, (state, action): void => {
        state.saving = false;
        state.error = action.payload ?? null;
      });
  },
});
export const { qualityBatch } = slice.actions;
export default slice.reducer;
